import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { Brain, Search, Link, Copy, Building2, Target, Share2, AlertTriangle } from 'lucide-react';
import { generateProfileName } from '@/utils/profileNameGenerator';

import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';
import { useToast } from '@/hooks/use-toast';
import InlineBehavioralChart from '@/components/recruitment/InlineBehavioralChart';
import { DashboardSkeleton } from './DashboardSkeleton';
import CompanySelector from '@/components/CompanySelector';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { generateRegistrationLink, generateWorkBehaviorLink, generateCognitiveAssessmentLink, copyToClipboard } from '@/utils/registrationLinks';
import { useSuspiciousThreshold } from '@/hooks/useSuspiciousThreshold';

interface Candidate {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  selected_words?: number[];
  completed_at: string;
  assessment_metadata?: {
    is_partial?: boolean;
    [key: string]: any;
  };
  assessment_variant?: string;
  status?: string;
  cognitivePercentile?: number | null;
}

interface MobileDashboardOverviewProps {
  candidates: Candidate[];
  loading: boolean;
  onViewCandidate: (candidate: Candidate) => void;
}

const MobileDashboardOverview: React.FC<MobileDashboardOverviewProps> = ({
  candidates,
  loading,
  onViewCandidate
}) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { currentCompany } = useCompany();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const companySlug = currentCompany?.company_slug || null;
  const { thresholdSeconds } = useSuspiciousThreshold();

  const handleCopyLink = async (linkType: 'registration' | 'workBehavior' | 'cognitive') => {
    if (!companySlug) return;
    const linkMap = {
      registration: { fn: () => generateRegistrationLink(companySlug), name: 'Full Assessment' },
      workBehavior: { fn: () => generateWorkBehaviorLink(companySlug), name: 'Work Behavior' },
      cognitive: { fn: () => generateCognitiveAssessmentLink(companySlug), name: 'Cognitive' },
    };
    const { fn, name } = linkMap[linkType];
    const success = await copyToClipboard(fn());
    toast({
      title: success ? "Link Copied!" : "Copy Failed",
      description: success ? `${name} link copied to clipboard` : "Unable to copy link",
      variant: success ? undefined : "destructive",
    });
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const filteredCandidates = candidates.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return c.full_name.toLowerCase().includes(term) || c.email.toLowerCase().includes(term);
  });

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="min-h-screen bg-background pb-20 overflow-x-hidden">
      {/* Mobile Header */}
      <div className="bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <CompanySelector />
          
          {/* Share Links Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Share2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[min(288px,calc(100vw-32px))] bg-background border shadow-lg z-50">
              <DropdownMenuItem 
                onClick={() => handleCopyLink('registration')}
                className="flex items-center justify-between cursor-pointer bg-primary/5 border-l-2 border-primary"
              >
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="font-medium">Full Assessment</span>
                  <Badge variant="secondary" className="text-xs px-1.5 py-0">Recommended</Badge>
                </div>
                <Copy className="h-4 w-4 flex-shrink-0" />
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={() => handleCopyLink('workBehavior')}
                className="flex items-center justify-between cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 flex-shrink-0" />
                  <span>Work Behavior Only</span>
                </div>
                <Copy className="h-4 w-4 flex-shrink-0" />
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleCopyLink('cognitive')}
                className="flex items-center justify-between cursor-pointer"
              >
                <div className="flex items-center gap-2">
                  <Brain className="h-4 w-4 flex-shrink-0" />
                  <span>Cognitive Only</span>
                </div>
                <Copy className="h-4 w-4 flex-shrink-0" />
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Search Bar */}
        <div className="mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search candidates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-10 bg-muted/50 border-border"
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 space-y-4 max-w-full overflow-x-clip">
        {filteredCandidates.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-base text-muted-foreground mb-6 leading-relaxed">
              {searchTerm ? 'No candidates match your search' : 'No candidates yet'}
            </p>
            {!searchTerm && (
              <Button onClick={() => navigate('/assessment')} className="text-sm px-6 py-3 min-h-[48px]">
                Take First Assessment
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredCandidates.map(candidate => {
              const wordCount = candidate.selected_words?.length || 0;
              const isPartial = candidate.assessment_metadata?.is_partial || candidate.status === 'partial';
              const isM2 = candidate.assessment_variant === 'model2';
              return (
                <Card key={candidate.id} className="cursor-pointer transition-shadow overflow-clip max-w-full w-full md:hover:shadow-md" onClick={() => onViewCandidate(candidate)}>
                  <CardContent className="p-4 py-5 overflow-hidden max-w-full w-full">
                    <div className="flex items-center gap-1 overflow-hidden max-w-full w-full">
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <h3 className="font-semibold text-card-foreground text-base truncate leading-relaxed min-w-0 mb-1">
                          {candidate.full_name}
                        </h3>
                        {isPartial && (
                          <div className="flex items-center gap-1 mb-1 min-w-0 overflow-hidden">
                            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded whitespace-nowrap flex-shrink-0">Partial</span>
                          </div>
                        )}
                        {candidate.scores && (
                          <p className="text-sm text-muted-foreground mb-1 truncate leading-relaxed">{generateProfileName(candidate)}</p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {new Date(candidate.completed_at).toLocaleDateString()}
                          </p>
                          {wordCount > 0 && <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded whitespace-nowrap">{wordCount} words</span>}
                          {candidate.cognitivePercentile && (
                            <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded whitespace-nowrap font-medium">
                              <Brain className="w-3 h-3" />
                              {candidate.cognitivePercentile}%
                              {candidate.cognitivePercentile >= 90 && (candidate as any).cognitiveDuration != null && (candidate as any).cognitiveDuration < thresholdSeconds && (
                                <span title={`Completed in ${Math.floor((candidate as any).cognitiveDuration / 60)}m ${(candidate as any).cognitiveDuration % 60}s — faster than normal`}><AlertTriangle className="h-3 w-3 text-amber-500" /></span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0 basis-[48px] min-w-[48px] max-w-[48px] overflow-hidden" style={{ contain: 'paint' }}>
                        <div className="h-[72px] w-[48px] overflow-hidden">
                          <InlineBehavioralChart scores={candidate.scores} assessmentVariant={candidate.assessment_variant} size="mobile-card" />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileDashboardOverview;